/**
 * TileDrawing.ts – Sprite-based tile rendering
 *
 * Provides a FULL mapping of all 234 tiles in the Kenney Micro-Roguelike
 * tilemap_packed.png (18 cols × 13 rows, 16×16 each).
 *
 * Each sprite index 0-233 maps to a unique character (tile char).
 * The 53 original ASCII chars are preserved for backward compatibility.
 * Remaining indices use Unicode chars starting at U+0100.
 *
 * Falls back to simple colored rectangles when sprites haven't loaded yet.
 */

import type { Theme } from '../types';
import { type GameAssets, getAssets } from './AssetLoader';

/* ─────────────────── Palette types ───────────────────────────────────────── */

export interface Palette {
  skyA: string;
  skyB: string;
  floor: string;
  floorAlt: string;
  wall: string;
  wallLight: string;
  wallShadow: string;
  accent: string;
  accentSoft: string;
  water: string;
}

export const PALETTES: Record<Theme, Palette> = {
  garden: {
    skyA: '#1d2b53',
    skyB: '#090d21',
    floor: '#222f61',
    floorAlt: '#26366d',
    wall: '#2f6e62',
    wallLight: '#4a9b85',
    wallShadow: '#1a4138',
    accent: '#ffc2dc',
    accentSoft: '#ffdff0',
    water: '#3d79b8',
  },
  letters: {
    skyA: '#222f53',
    skyB: '#0a0f24',
    floor: '#253161',
    floorAlt: '#2a3a73',
    wall: '#6b7fbe',
    wallLight: '#95a8db',
    wallShadow: '#3a4a86',
    accent: '#c7dcff',
    accentSoft: '#e4eeff',
    water: '#4f78c9',
  },
  sanctuary: {
    skyA: '#34284a',
    skyB: '#100d1e',
    floor: '#3f335a',
    floorAlt: '#4a3b67',
    wall: '#b29da3',
    wallLight: '#d6c1c7',
    wallShadow: '#735f67',
    accent: '#ffd4b4',
    accentSoft: '#ffe8d8',
    water: '#6b8cc4',
  },
};

/* ─────────────────── TOTAL TILE COUNT ──────────────────────────────────── */

export const TILE_COUNT = 234; // 18 cols × 13 rows

/* ─────────────────── Legacy char → index (backward compat) ─────────────── */

/** The 53 original tile chars preserved for existing level templates. */
const LEGACY_CHAR_TO_INDEX: Record<string, number> = {
  '.': 19, P: 154, C: 150, M: 13, T: 28, R: 151, L: 130,
  '#': 3, W: 22, B: 4, S: 91, I: 100, G: 144, H: 8, F: 14,
  '~': 62,
  '1': 82, '2': 64, '3': 65, '4': 77, '5': 79, '6': 66, '7': 51, '8': 52, '9': 208, '0': 67,
  ',': 225, b: 85, s: 86, h: 206, r: 229, c: 152, p: 199, f: 45, l: 188, g: 80, d: 207,
  k: 211, v: 228, t: 227, i: 212, n: 189, o: 196, u: 226, x: 124, e: 159, j: 68, q: 84,
  y: 200, z: 178, w: 153, a: 181, m: 40,
};

/* ─────────────────── Bidirectional char ↔ index mappings ────────────────── */

/** Char for each sprite index 0-233. */
const INDEX_TO_CHAR: string[] = new Array(TILE_COUNT);
/** Sprite index for each char. */
const CHAR_TO_INDEX = new Map<string, number>();

// Populate with legacy mappings first
for (const [ch, idx] of Object.entries(LEGACY_CHAR_TO_INDEX)) {
  INDEX_TO_CHAR[idx] = ch;
  CHAR_TO_INDEX.set(ch, idx);
}
// Fill remaining indices with Unicode chars (U+0100+)
let _nextCode = 0x0100;
for (let idx = 0; idx < TILE_COUNT; idx++) {
  if (INDEX_TO_CHAR[idx] === undefined) {
    // Skip codepoints that collide with existing chars (shouldn't happen)
    while (CHAR_TO_INDEX.has(String.fromCharCode(_nextCode))) _nextCode++;
    const ch = String.fromCharCode(_nextCode++);
    INDEX_TO_CHAR[idx] = ch;
    CHAR_TO_INDEX.set(ch, idx);
  }
}

/** Get the tile char for a given sprite index. */
export function charForIndex(index: number): string {
  return INDEX_TO_CHAR[index] ?? '.';
}

/** Get the sprite index for a given tile char (-1 if unknown). */
export function indexForChar(ch: string): number {
  return CHAR_TO_INDEX.get(ch) ?? -1;
}

/* ─────────────────── Public tile char array (all 234) ──────────────────── */

/** All tile chars, ordered by sprite index 0-233. */
export const TILE_CHARS: readonly string[] = [...INDEX_TO_CHAR];
export type TileChar = string;

/* ─────────────────── Tile labels (all 234) ─────────────────────────────── */

/** Row-based label prefixes for the spritesheet. */
const ROW_LABELS: string[] = [
  'Wall Top',       // Row 0
  'Wall Base',      // Row 1
  'Object',         // Row 2
  'Object',         // Row 3
  'Item',           // Row 4
  'Pattern',        // Row 5
  'Pattern',        // Row 6
  'Floor',          // Row 7
  'Deco',           // Row 8
  'Deco',           // Row 9
  'Overlay',        // Row 10
  'Sprite',         // Row 11
  'Effect',         // Row 12
];

function generateLabel(index: number): string {
  const row = Math.floor(index / 18);
  const prefix = ROW_LABELS[row] ?? 'Tile';
  return `${prefix} ${index}`;
}

/** Legacy labels kept for existing chars. */
const LEGACY_LABELS: Record<string, string> = {
  '.': 'Floor', '#': 'Wall', '~': 'Water',
  ',': 'Garden Deco', b: 'Book', s: 'Stone',
  h: 'Heart', r: 'Rose', c: 'Candle', p: 'Petals',
  f: 'Fountain', l: 'Love Letter', g: 'Gift Box', d: 'Diamond',
  k: 'Kiss Mark', v: 'Vine', t: 'Cherry Tree', i: 'Ribbon',
  n: 'Ring', o: 'Ornament', u: 'Butterfly', x: 'Sparkles',
  e: 'Lantern', j: 'Jewel Box', q: 'Cupid Arrow', y: 'Lily',
  z: 'Fairy Lights', w: 'Wedding Bell', a: 'Angel Wing', m: 'Moonlight',
  W: 'Wood Wall', B: 'Brick Wall', S: 'Stone Wall', I: 'Iron Wall',
  G: 'Glass Window', H: 'Hedge Wall', F: 'Fence',
  P: 'Wood Planks', C: 'Carpet', M: 'Marble Floor',
  T: 'Ceramic Tile', R: 'Rug', L: 'Lace Floor',
  '1': 'Chair', '2': 'Table', '3': 'Sofa', '4': 'Bed',
  '5': 'Wardrobe', '6': 'Vanity', '7': 'Bookshelf', '8': 'Piano',
  '9': 'Fireplace', '0': 'Floor Lamp',
};

export const TILE_LABELS: Record<string, string> = {};
for (let idx = 0; idx < TILE_COUNT; idx++) {
  const ch = INDEX_TO_CHAR[idx];
  TILE_LABELS[ch] = LEGACY_LABELS[ch] ?? generateLabel(idx);
}

/* ─────────────────── Tile classification sets ──────────────────────────── */

/**
 * Classify tiles by sprite index ranges.
 * Rows 0-1 (0-35): walls  |  Row 7 some: floors  |  Rows 5-6 some: walls
 * Row 2-4 heavy objects: furniture  |  The rest: overlays or floor
 */

// Wall indices: rows 0-1, rows 5-6 (patterned walls)
const WALL_INDICES = new Set<number>();
for (let i = 0; i <= 35; i++) WALL_INDICES.add(i);          // rows 0-1
for (let i = 90; i <= 125; i++) WALL_INDICES.add(i);        // rows 5-6
// Also add row 8 full solid tiles
WALL_INDICES.add(144); WALL_INDICES.add(145); WALL_INDICES.add(146);
WALL_INDICES.add(147); WALL_INDICES.add(148);
WALL_INDICES.add(153); WALL_INDICES.add(154); WALL_INDICES.add(155);

// Floor indices: row 7 (126-143), plus some specific custom floor tiles
const FLOOR_INDICES = new Set<number>([
  126, 127, 128, 129, 130, 131, 132, 133, 134, 135,
  136, 137, 138, 139, 140, 141, 142, 143,
  149, 150, 151, // row 8 floor-ish tiles
]);

// Furniture indices: rows 2-4 heavier objects
const FURNITURE_INDICES = new Set<number>();
for (let i = 36; i <= 89; i++) FURNITURE_INDICES.add(i);

// Overlay indices: rows 9-12 (sparse decorations)
const OVERLAY_INDICES = new Set<number>();
for (let i = 156; i <= 233; i++) OVERLAY_INDICES.add(i);
// Also row 8 remaining sparse tiles
for (let i = 152; i <= 161; i++) {
  if (!WALL_INDICES.has(i) && !FLOOR_INDICES.has(i)) OVERLAY_INDICES.add(i);
}

// Build char sets from index sets (include legacy chars too)
const WALL_CHARS = new Set<string>();
const CUSTOM_FLOOR_CHARS = new Set<string>();
const FURNITURE_CHARS = new Set<string>();
const OVERLAY_CHARS = new Set<string>();

for (const idx of WALL_INDICES) WALL_CHARS.add(INDEX_TO_CHAR[idx]);
for (const idx of FLOOR_INDICES) CUSTOM_FLOOR_CHARS.add(INDEX_TO_CHAR[idx]);
for (const idx of FURNITURE_INDICES) FURNITURE_CHARS.add(INDEX_TO_CHAR[idx]);
for (const idx of OVERLAY_INDICES) OVERLAY_CHARS.add(INDEX_TO_CHAR[idx]);

// Ensure legacy classifications override the index-based ones
// (e.g. '#'=3 is a wall, '.'/19 is floor, '~'/62 is water)
WALL_CHARS.add('#'); WALL_CHARS.add('W'); WALL_CHARS.add('B'); WALL_CHARS.add('S');
WALL_CHARS.add('I'); WALL_CHARS.add('G'); WALL_CHARS.add('H'); WALL_CHARS.add('F');
CUSTOM_FLOOR_CHARS.add('P'); CUSTOM_FLOOR_CHARS.add('C'); CUSTOM_FLOOR_CHARS.add('M');
CUSTOM_FLOOR_CHARS.add('T'); CUSTOM_FLOOR_CHARS.add('R'); CUSTOM_FLOOR_CHARS.add('L');
// '.' is the default floor — not in CUSTOM_FLOOR_CHARS, not a wall, not furniture, not overlay
WALL_CHARS.delete('.'); CUSTOM_FLOOR_CHARS.delete('.'); FURNITURE_CHARS.delete('.'); OVERLAY_CHARS.delete('.');
// '~' is water — classified as solid
WALL_CHARS.add('~');

export function isSolidChar(tile: string): boolean {
  return WALL_CHARS.has(tile) || FURNITURE_CHARS.has(tile);
}

export function isOverlayChar(tile: string): boolean {
  return OVERLAY_CHARS.has(tile);
}

export function isFloorChar(tile: string): boolean {
  return tile === '.' || CUSTOM_FLOOR_CHARS.has(tile);
}

/* ─────────────────── Editor tile categories ─────────────────────────────── */

export interface TileCategory {
  label: string;
  icon: string;
  indices: number[];
  open?: boolean;
}

/** Categories for the editor palette, organized by spritesheet rows. */
export const TILE_CATEGORIES: TileCategory[] = [
  { label: 'Walls Purple',  icon: '▦', indices: [0,1,2,3,4, 18,19,20,21,22], open: true },
  { label: 'Walls Teal',    icon: '▦', indices: [5,6,7,8,9, 23,24,25,26,27], open: true },
  { label: 'Walls Slate',   icon: '▦', indices: [10,11,12,13,14,15,16,17, 28,29,30,31,32,33,34,35] },
  { label: 'Objects 1',     icon: '⊞', indices: Array.from({length: 18}, (_, i) => 36 + i), open: true },
  { label: 'Objects 2',     icon: '⊞', indices: Array.from({length: 18}, (_, i) => 54 + i) },
  { label: 'Items',         icon: '⚔', indices: Array.from({length: 18}, (_, i) => 72 + i) },
  { label: 'Patterns Purple', icon: '▩', indices: [90,91,92,93,94, 108,109,110,111,112] },
  { label: 'Patterns Teal',   icon: '▩', indices: [95,96,97,98,99, 113,114,115,116,117] },
  { label: 'Patterns Slate',  icon: '▩', indices: [100,101,102,103,104,105,106,107, 118,119,120,121,122,123,124,125] },
  { label: 'Floors',        icon: '▤', indices: Array.from({length: 18}, (_, i) => 126 + i), open: true },
  { label: 'Decor 1',       icon: '✦', indices: Array.from({length: 18}, (_, i) => 144 + i) },
  { label: 'Decor 2',       icon: '✦', indices: Array.from({length: 18}, (_, i) => 162 + i) },
  { label: 'Overlays',      icon: '◈', indices: Array.from({length: 18}, (_, i) => 180 + i) },
  { label: 'Sprites',       icon: '❋', indices: Array.from({length: 18}, (_, i) => 198 + i) },
  { label: 'Effects',       icon: '◇', indices: Array.from({length: 18}, (_, i) => 216 + i) },
];

/* ─────────────────── Sprite mapping ─────────────────────────────────────── */

/** Resolve a tile char to its sprite index. Returns -1 for unknown chars. */
function spriteIndexForChar(tile: string): number {
  return CHAR_TO_INDEX.get(tile) ?? -1;
}

/* ─────────────────── Floor sprite index ──────────────────────────────────── */

/** Default floor tile index — used when drawing floor under overlays. */
const FLOOR_SPRITE = CHAR_TO_INDEX.get('.')!;   // index 19

/* ─────────────────── Drawing functions ──────────────────────────────────── */

/**
 * Draws just a floor tile at (px, py) at the given tileSize.
 * Used as "pass 1" underneath overlay decorations.
 */
export function drawFloorAt(
  ctx: CanvasRenderingContext2D,
  _x: number,
  _y: number,
  px: number,
  py: number,
  tileSize: number,
  palette: Palette,
): void {
  const assets = getAssets();
  if (assets) {
    assets.tiles.draw(ctx, FLOOR_SPRITE, px, py, tileSize, tileSize);
  } else {
    ctx.fillStyle = palette.floor;
    ctx.fillRect(px, py, tileSize, tileSize);
  }
}

/**
 * Draw a tile sprite directly by index (used by editor palette swatches).
 */
export function drawTileSpriteByIndex(
  ctx: CanvasRenderingContext2D,
  index: number,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
  rotation: number = 0,
): void {
  const assets = getAssets();
  if (!assets) return;
  assets.tiles.draw(ctx, index, dx, dy, dw, dh, rotation);
}

/**
 * Draws a single tile (sprite-based when assets loaded, fallback otherwise).
 */
export function drawTile(
  ctx: CanvasRenderingContext2D,
  tile: string,
  x: number,
  y: number,
  px: number,
  py: number,
  tileSize: number,
  palette: Palette,
  _elapsed: number,
  rotation: number = 0,
): void {
  const assets = getAssets();

  if (assets) {
    drawTileSprite(ctx, tile, x, y, px, py, tileSize, assets, rotation);
  } else {
    drawTileFallback(ctx, tile, x, y, px, py, tileSize, palette);
  }
}

/* ── Sprite-based tile drawing ── */
function drawTileSprite(
  ctx: CanvasRenderingContext2D,
  tile: string,
  _x: number,
  _y: number,
  px: number,
  py: number,
  tileSize: number,
  assets: GameAssets,
  rotation: number = 0,
): void {
  const isCustomFloor = CUSTOM_FLOOR_CHARS.has(tile);
  const isOverlay = OVERLAY_CHARS.has(tile);

  // Draw floor background first (also under walls for edge tile transparency)
  if (!isCustomFloor && !isOverlay) {
    assets.tiles.draw(ctx, FLOOR_SPRITE, px, py, tileSize, tileSize);

    // For plain floor tile '.', that IS the tile — done
    if (tile === '.' || indexForChar(tile) === FLOOR_SPRITE) return;
  }

  // Look up sprite index
  const idx = spriteIndexForChar(tile);
  if (idx < 0) {
    // Unknown tile: draw a pink error square
    ctx.fillStyle = '#ff00ff';
    ctx.fillRect(px, py, tileSize, tileSize);
    return;
  }

  // Draw the actual tile sprite (with rotation)
  if (idx !== FLOOR_SPRITE) {
    assets.tiles.draw(ctx, idx, px, py, tileSize, tileSize, rotation);
  }
}

/* ── Fallback drawing (before assets load) ── */
function drawTileFallback(
  ctx: CanvasRenderingContext2D,
  tile: string,
  _x: number,
  _y: number,
  px: number,
  py: number,
  tileSize: number,
  palette: Palette,
): void {
  const isWall = WALL_CHARS.has(tile);
  const isCustomFloor = CUSTOM_FLOOR_CHARS.has(tile);
  const isOverlay = OVERLAY_CHARS.has(tile);

  if (!isWall && !isCustomFloor && !isOverlay) {
    ctx.fillStyle = palette.floor;
    ctx.fillRect(px, py, tileSize, tileSize);
  }

  if (isWall) {
    ctx.fillStyle = palette.wall;
    ctx.fillRect(px, py, tileSize, tileSize);
    ctx.fillStyle = palette.wallLight;
    ctx.fillRect(px, py, tileSize, Math.max(1, tileSize / 4));
    return;
  }

  if (isCustomFloor) {
    ctx.fillStyle = palette.floor;
    ctx.fillRect(px, py, tileSize, tileSize);
    return;
  }

  if (FURNITURE_CHARS.has(tile)) {
    ctx.fillStyle = '#6a5040';
    ctx.fillRect(px + 1, py + 1, tileSize - 2, tileSize - 2);
    return;
  }

  if (isOverlay) {
    ctx.fillStyle = palette.accent;
    const s = Math.max(1, Math.floor(tileSize * 0.25));
    ctx.fillRect(px + s, py + s, tileSize - s * 2, tileSize - s * 2);
    return;
  }
}

/* ─────────────────── Background (starry sky) ────────────────────────────── */

export function drawBackground(
  ctx: CanvasRenderingContext2D,
  palette: Palette,
  elapsed: number,
  width: number,
  height: number,
): void {
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, palette.skyA);
  gradient.addColorStop(1, palette.skyB);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  // Twinkling stars
  ctx.fillStyle = '#f9e8ff';
  for (let i = 0; i < 56; i += 1) {
    const sx = (i * 71.37) % width;
    const sy = (i * 41.19 + elapsed * ((i % 3) + 1) * 1.3) % height;
    ctx.fillRect(Math.floor(sx), Math.floor(sy), 1, 1);
  }
}
